import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Image,
  Icon,
  CircularProgress,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { FaRegCircleCheck } from "react-icons/fa6";

import { confirmBooking, getBookingStatus } from "../services/booking";
import { getCookie } from "../core/utils";
import { OpenInAppBanner } from "../components/OpenInAppBanner";
import LogoIcon from "../assets/images/logoIcon.png";
import LogoText from "../assets/images/logoText.png";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;

export const ConfirmBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const jwt = getCookie("book-d booking token");

  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [fetchingStatus, setFetchingStatus] = useState(true);

  // iOS Smart App Banner — shows a native prompt in Safari, no JS redirect
  useEffect(() => {
    if (!isIOS || !id) return;
    const meta = document.createElement("meta");
    meta.name = "apple-itunes-app";
    meta.content = `app-id=6558535790, app-argument=bookd://confirm-booking/${id}`;
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, [id]);

  useEffect(() => {
    if (!jwt) {
      navigate("/login", {
        state: { returnTo: `/confirm-booking/${id}` },
        replace: true,
      });
    }
  }, [jwt, id, navigate]);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!id || !jwt) return;

      try {
        const res = await getBookingStatus(id);

        if (
          res?.status === 200 &&
          res?.data?.isServiceAvailedByCustomer &&
          res.data.isServiceAvailedByCustomer !== "Pending"
        ) {
          setConfirmed(true);
        }
      } catch {
        // silently fail — user can still confirm manually
      }

      setFetchingStatus(false);
    };

    fetchStatus();
  }, [id, jwt]);

  const handleConfirm = async () => {
    if (!id) return;

    setLoading(true);

    try {
      const res = await confirmBooking(id);

      if (res?.status === 200) {
        setConfirmed(true);
        toast({
          title: "Booking Confirmed",
          description:
            res.message || "Your booking has been confirmed successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: "An error occurred",
          description:
            res?.message || "Unable to confirm booking. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch {
      toast({
        title: "An error occurred",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }

    setLoading(false);
  };

  if (!jwt) return null;

  return (
    <Box
      display="flex"
      flexDir="column"
      minH="100vh"
      bg="linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #FFF7F3 70%, #D8F1F2 100%)"
    >
      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        minH="72px"
        px={{ base: "20px", md: "10%" }}
        align="center"
        justify="space-between"
        bg="white"
      >
        <Flex align="center" gap="16px">
          <Icon
            fontSize={24}
            color="#19202D"
            cursor="pointer"
            onClick={() => navigate("/")}
          >
            <IoIosArrowBack />
          </Icon>

          <Image
            src={LogoIcon}
            alt="Book-d Logo"
            h="32px"
            w="19px"
            cursor="pointer"
            onClick={() => navigate("/")}
          />
        </Flex>
      </Flex>

      {isMobile && (
        <OpenInAppBanner
          deepLink={`bookd://confirm-booking/${id}`}
          isIOS={isIOS}
        />
      )}

      <Flex flex={1} align="center" justify="center" px="20px" py="40px">
        <Flex
          flexDir="column"
          align="center"
          maxW="520px"
          w="100%"
          bg="white"
          borderRadius="24px"
          boxShadow="0px 4px 20px rgba(0, 0, 0, 0.08)"
          p={{ base: "32px 24px", md: "48px 40px" }}
        >
          {fetchingStatus ? (
            <Flex py="40px">
              <CircularProgress isIndeterminate color="#FF8E3C" size="60px" />
            </Flex>
          ) : confirmed ? (
            <>
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#DBF1F2"
                align="center"
                justify="center"
                mb="24px"
              >
                <FaRegCircleCheck size={44} color="#267684" />
              </Flex>

              <Heading
                fontSize={{ base: "24px", md: "28px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                textAlign="center"
              >
                Booking Confirmed
              </Heading>

              <Box
                h="3px"
                w="60px"
                borderRadius="10px"
                bg="#FF8E3C"
                my="20px"
              />

              <Text
                fontSize={{ base: "15px", md: "16px" }}
                color="#4F4F51"
                lineHeight="26px"
                textAlign="center"
              >
                Thank you! Your booking has been successfully confirmed. You can
                manage your appointments from the Book-d app.
              </Text>

              <Button
                mt="32px"
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="52px"
                px="40px"
                fontSize="16px"
                fontFamily="RedHatDisplay-Bold"
                onClick={() => navigate("/")}
              >
                Back to Home
              </Button>
            </>
          ) : (
            <>
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#FFF0E5"
                align="center"
                justify="center"
                mb="24px"
              >
                <Text fontSize="36px" lineHeight={1}>
                  📋
                </Text>
              </Flex>

              <Heading
                fontSize={{ base: "24px", md: "28px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                textAlign="center"
              >
                Confirm Booking
              </Heading>

              <Box
                h="3px"
                w="60px"
                borderRadius="10px"
                bg="#FF8E3C"
                my="20px"
              />

              <Text
                fontSize={{ base: "15px", md: "16px" }}
                color="#4F4F51"
                lineHeight="26px"
                textAlign="center"
                maxW="380px"
              >
                Please click the button below to confirm that this booking took
                place. This is important for dispute management and Book-d's
                records.
              </Text>

              <Button
                mt="32px"
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="58px"
                w={{ base: "100%", md: "280px" }}
                fontSize="17px"
                fontFamily="RedHatDisplay-Bold"
                onClick={handleConfirm}
                isLoading={loading}
                loadingText="Confirming..."
                disabled={loading}
              >
                Confirm Booking
              </Button>
            </>
          )}

          {!fetchingStatus && (
            <Flex align="center" gap="6px" mt="40px" opacity={0.5}>
              <Text fontSize="13px" color="#4F4F51">
                Powered by
              </Text>
              <Image src={LogoText} h="18px" objectFit="contain" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
